import { Grid, Paper, Typography } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import {
  IIASDashboardPieChartDataItems,
  IIASDashboardQueryParams,
  IIASDashboardSectionSecondaryChartInfo,
} from '~/src/dash/views/ias-publisher/IASDashboard/common/types';

const BarLabel = ({ x, y, width, height, value, isRate }: Record<string, any>) => {
  const valueString = `${convertLargeNumbersToRoundedString(value)}${isRate ? '%' : ''}`;

  return (
    <text
      x={x + width + 4}
      y={y + height / 2 + 5}
      fill={'gray'}
      textAnchor='start'
      style={{ fontSize: 12 }}
    >
      {valueString}
    </text>
  );
};

const PieSliceLabel = ({ cx, cy, x, y, name, value }: Record<string, any>) => {
  return (
    <text
      x={x}
      y={y}
      fill='black'
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline={y < cy ? 'central' : 'hanging'}
      style={{ margin: 10 }}
    >
      <tspan
        x={x > cx ? x + 8 : x - 8}
        dy='-1em'
        style={{
          fill: 'black',
        }}
      >
        {name}
      </tspan>
      <tspan
        x={x > cx ? x + 8 : x - 8}
        dy='1.2em'
        style={{
          fill: 'gray',
        }}
      >
        {convertLargeNumbersToRoundedString(value)}
      </tspan>
    </text>
  );
};

const renderChart = (
  type: 'horizontalBar' | 'pie',
  chartData: ISecondaryChartData[],
  colorBank: string[],
  isRate: boolean,
) => {
  const valueKey = isRate ? 'Rate' : 'Value';
  switch (type) {
    case 'horizontalBar':
      return (
        <BarChart
          data={chartData}
          layout='vertical'
          height={400}
          margin={{ top: 48, right: 16, bottom: 16, left: 16 }}
          style={{ width: '100%' }}
        >
          <XAxis
            type='number'
            tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
            tickFormatter={convertLargeNumbersToRoundedString}
          />
          <YAxis
            type='category'
            dataKey='name'
            width={120}
            tick={{ fontSize: 12, fill: 'black' }}
          />
          <Tooltip
            formatter={(value) => value && `${value.toLocaleString()}${isRate ? '%' : ''}`}
          />
          <Bar dataKey={valueKey} barSize={32}>
            {chartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colorBank[index % 20]} />
            ))}
            <LabelList
              dataKey={valueKey}
              // @ts-ignore
              content={<BarLabel isRate={isRate} />}
              position='right'
            />
          </Bar>
        </BarChart>
      );
    case 'pie':
      return (
        <PieChart height={400} style={{ width: '100%' }}>
          <Pie
            data={chartData}
            dataKey='Value'
            nameKey='name'
            cx='50%'
            cy='45%'
            innerRadius={96}
            outerRadius={120}
            label={PieSliceLabel}
          >
            {chartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colorBank[index % colorBank.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => value && value.toLocaleString()} />
          <Legend />
        </PieChart>
      );
    default:
      return null;
  }
};

interface ISecondaryChartData {
  name: string;
  Value?: number;
  Rate?: number;
}

const deviceTypeMap = {
  111: 'Desktop',
  112: 'Desktop',
  221: 'Mobile',
  222: 'Mobile',
  121: 'Mobile',
  122: 'Mobile',
  231: 'Tablet',
  232: 'Tablet',
  131: 'Tablet',
  132: 'Tablet',
  252: 'Connected TV',
} as Record<string, string>;

// Notes: This is custom built only for device type. May need to make this more dynamic later.
const extractPieChartDataFromData = (
  data: Array<Record<string, any>>,
  pieChartDataItems: IIASDashboardPieChartDataItems,
) => {
  const { groupedByKey, valueKey } = pieChartDataItems;
  const chartData = [] as ISecondaryChartData[];
  const combinedData: Record<string, number> = {};
  data.map((item) => {
    if (item[groupedByKey] && deviceTypeMap[[item[groupedByKey]].toString()]) {
      const deviceType = deviceTypeMap[[item[groupedByKey]].toString()];
      if (combinedData[deviceType]) {
        combinedData[deviceType] += item[valueKey] || 0;
      } else {
        combinedData[deviceType] = item[valueKey] || 0;
      }
    }
  });
  Object.entries(combinedData).map((item) => {
    chartData.push({
      name: item[0],
      Value: item[1],
    });
  });
  return chartData.sort(
    (a: ISecondaryChartData, b: ISecondaryChartData) => (b.Value || 0) - (a.Value || 0),
  );
};

const extractHBarChartDataFromData = (
  data: Array<Record<string, any>>,
  dataItems: Record<string, string>,
  isRate: boolean = false,
) => {
  const chartData = [] as ISecondaryChartData[];
  const valueKey = isRate ? 'Rate' : 'Value';
  Object.keys(dataItems).map((key) => {
    chartData.push({
      name: dataItems[key],
      [valueKey]: data[0][key],
    });
  });
  return chartData.sort(
    (a: ISecondaryChartData, b: ISecondaryChartData) => (b[valueKey] || 0) - (a[valueKey] || 0),
  );
};

interface IIASDashboardSecondaryChartProps {
  classes: Record<string, string>;
  colors: Record<string, string>;
  queryParams: IIASDashboardQueryParams;
  chartInfo: IIASDashboardSectionSecondaryChartInfo;
  isComparedToEnabled: boolean;
}

export const IASDashboardSecondaryChart = ({
  // isComparedToEnabled,
  classes,
  colors,
  queryParams,
  chartInfo,
}: IIASDashboardSecondaryChartProps) => {
  const isMounted = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  // Notes: These are held in state to ensure charts display without error
  const [params, setParams] = useState(queryParams);
  const [info, setInfo] = useState(chartInfo);
  const [data, setData] = useState<any>(null);
  const [chartData, setChartData] = useState<ISecondaryChartData[]>([]);

  const { graph, title, type, isRate, barChartDataItems, pieChartDataItems } = info;

  const colorBank = Object.values(colors);

  // on mount, we set the load state
  useEffect(() => {
    setLoadState(LoadingStateLoading);
  }, []);

  // once we receive the params prop, we set it to state
  useEffect(() => {
    // this needs a delay as a fix for the rechart pie animation bug
    setTimeout(() => {
      setParams(queryParams);
    }, 1000);
  }, [queryParams]);

  // once we recevive the chart info props, we set it to state
  useEffect(() => {
    setInfo(chartInfo);
  }, [chartInfo]);

  // whenever the params or info changes, we run the api call
  useEffect(() => {
    if (!isEmpty(params) && !isEmpty(info)) {
      runAggregateQuery();
    }
  }, [params, info]);

  // whenever the data or chart info changes, we set the chart data
  useEffect(() => {
    if (isMounted.current && data && data.length > 0) {
      switch (type) {
        case 'horizontalBar':
          if (!isEmpty(barChartDataItems)) {
            setChartData(extractHBarChartDataFromData(data[0], barChartDataItems || {}, isRate));
          }
          break;
        case 'pie':
          if (!isEmpty(pieChartDataItems)) {
            setChartData(
              extractPieChartDataFromData(
                data[0],
                pieChartDataItems || ({} as IIASDashboardPieChartDataItems),
              ),
            );
          }
          break;
        default:
          setChartData([]);
          break;
      }
    } else {
      isMounted.current = true;
    }
  }, [data, chartInfo]);

  // whenever the chart data changes, we set the correct load state
  useEffect(() => {
    if (isMounted.current) {
      setLoadState(chartData && chartData.length > 0 ? LoadingStateHasData : LoadingStateLoading);
    }
  }, [chartData]);

  // Notes: Commented out code below is for comparison data to be implemented at a later date
  const runAggregateQuery = () => {
    setLoadState(LoadingStateLoading);
    const {
      startDate,
      endDate,
      // compStartDate,
      // compEndDate,
      timezone,
      groupingNumber,
      deviceTypes,
      teamID,
      anIDs,
      publisherID,
    } = params;

    const body1 = {
      GraphID: graph,
      StartDate: startDate,
      EndDate: endDate,
      Timezone: timezone,
      GroupingNumber: groupingNumber,
      DeviceTypes: deviceTypes,
      TeamID: teamID,
      AnIDs: anIDs,
    } as IIASFirstAndThirdPartyRequestBody;
    // const body2 = {
    //   GraphID: graph,
    //   StartDate: compStartDate,
    //   EndDate: compEndDate,
    //   Timezone: timezone,
    //   GroupingNumber: groupingNumber,
    //   DeviceTypes: deviceTypes,
    //   Filters: filters,
    //   TeamID: teamID,
    //   AnIDs: anIDs,
    // } as IIASFirstAndThirdPartyRequestBody;

    const queries = [
      queryFirstAndThirdPartyIasData(body1, { publisher_id: publisherID }),
      // queryFirstAndThirdPartyIasData(body2, { publisher_id: publisherID }),
    ];

    // Runs the queries together in Promise All
    Promise.all(queries)
      .then((data: any[][]) => {
        let queryFailed = false;

        // if the call is successful but there is no data
        if (!data[0][0].rows) {
          queryFailed = true;
          setLoadState(LoadingStateNoData);
          return;
        }

        const data1 = [] as any;
        data1.push(data[0][0].rows);
        // data1.push(data[1][0].rows);

        // check the data to make sure it's there, otherwise show an error msg
        if (!data1[0].rows) {
          if (data1[0].length === 0) {
            queryFailed = true;
            // if (!isComparedToEnabled && formatDateStartDay(endDate) === formatDateStartDay(startDate)) {
            //   setLoadState(LoadingStateNoData);
            // } else {
            //   setLoadState(LoadingStateNoData);
            // }
            setLoadState(LoadingStateNoData);
            // if there is no compare data (query went wrong or publisher is new), reload the graph without compare
          }
          // } else if (isComparedToEnabled && (!data1[1] || (data1[1] && data1[1].length === 0))) {
          //   queryFailed = true;

          // setLoadState(LoadingStateNoData);
          // }
        }
        if (queryFailed) {
          return;
        }

        unstable_batchedUpdates(() => {
          setData([
            data1[0] ? data1[0] : [],
            // !!data1[1] ? data1[1] : [],
          ]);
          // setLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        setLoadState(LoadingStateNoData);
      });
  };

  return (
    <div>
      <Paper className={classes.paper}>
        <Grid container alignItems='center' spacing={1}>
          <Grid item container justify='center' xs={12}>
            <Typography className={classes.chartName}>{title || ''}</Typography>
          </Grid>

          {loadState === LoadingStateHasData ? (
            <ResponsiveContainer width='99%' height={400}>
              {/* must be a render function due to the recharts js library */}
              {renderChart(type, chartData, colorBank, isRate || false)}
            </ResponsiveContainer>
          ) : (
            <Grid container alignItems='center' direction='column'>
              <Grid
                item
                style={{
                  width: '99%',
                  height: 400,
                  textAlign: 'center',
                }}
              >
                <Typography style={{ marginTop: 150 }}>
                  {loadState === LoadingStateNoData
                    ? 'No data to display for the selected period or filters'
                    : 'Loading query...'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </div>
  );
};
